// Approximate display rates (marked ≈ in the UI) — for presentation, not invoicing.
export const ISK_PER_EUR = 147;
export const ISK_PER_USD = 137;

// Icelandic money: a comma with 1–2 trailing digits is the decimal mark ("62.000,00" -> 62000);
// a dot or comma before a 3-digit group is a thousands separator ("25.000", "35,000").
const parseNum = (numStr) => {
  const s = String(numStr);
  const dec = s.match(/,(\d{1,2})$/);
  const intPart = (dec ? s.slice(0, dec.index) : s).replace(/[^\d-]/g, "");
  if (!intPart || intPart === "-") return null;
  const n = parseInt(intPart, 10);
  if (Number.isNaN(n)) return null;
  return dec ? n + parseInt(dec[1], 10) / Math.pow(10, dec[1].length) : n;
};

export const parsePrice = (str) => {
  if (!str) return null;
  const s = String(str);
  const m =
    s.match(/(\d[\d.,\s ]*\d|\d)\s*(ISK|EUR|USD)\b/i) ||
    s.match(/\b(ISK|EUR|USD)\s*(\d[\d.,\s ]*\d|\d)/i);
  if (!m) return null;
  const firstIsCur = /^[A-Za-z]{3}$/.test(m[1]);
  const numStr = firstIsCur ? m[2] : m[1];
  const currency = (firstIsCur ? m[1] : m[2]).toUpperCase();
  const amount = parseNum(numStr);
  if (amount === null) return null;
  const idx = s.toUpperCase().indexOf(currency);
  const prefix = firstIsCur ? "" : s.slice(0, m.index).replace(/[$€£]\s*$/, "").trim();
  return { amount, currency, prefix: prefix ? `${prefix} ` : "" };
};

export const toIsk = (p) =>
  p.currency === "ISK" ? p.amount : p.currency === "EUR" ? p.amount * ISK_PER_EUR : p.amount * ISK_PER_USD;

export const toUsd = (p) =>
  p.currency === "USD" ? p.amount : p.currency === "EUR" ? (p.amount * ISK_PER_EUR) / ISK_PER_USD : p.amount / ISK_PER_USD;

// Whole numbers everywhere — sub-1-ISK money no longer exists, decimals only confuse.
export const fmtIsk = (n) => `${Math.round(Number(n)).toLocaleString("de-DE")} ISK`;
export const fmtIsk0 = fmtIsk;

// plain number with Icelandic grouping, no decimals — for every currency figure
export const fmtNum2 = (n) => Math.round(Number(n)).toLocaleString("de-DE");

// Canonical display for a money INPUT box: thousands separators, no decimals, always.
// Expressions evaluate first ("25.000*4"), currency codes are kept ("1.000 EUR" -> "1.000 EUR"),
// plain text ("On request") passes through untouched. No currency conversion — display only.
export const fmtMoneyBox = (v) => {
  const s = String(v || "").trim();
  if (!s) return v;
  const expr = evalMoneyExpr(s);
  const coded = parsePrice(expr ?? s);
  const p = coded || parseMoney(expr ?? s);
  if (!p) return v;
  const num = Math.round(p.amount).toLocaleString("de-DE");
  return coded ? `${p.prefix || ""}${num} ${coded.currency}` : num;
};
export const fmtUsdApprox = (n) => `≈ $${Math.round(Number(n)).toLocaleString("en-US")}`;
export const fmtEurApprox = (n) => `≈ €${Math.round(Number(n)).toLocaleString("en-US")}`;

export const toEur = (p) =>
  p.currency === "EUR" ? p.amount : p.currency === "USD" ? (p.amount * ISK_PER_USD) / ISK_PER_EUR : p.amount / ISK_PER_EUR;

// Secondary conversion currency depends on the client's region: Europe -> EUR, rest of world -> USD
export const secondPrice = (p, region) =>
  region === "eu" ? fmtEurApprox(toEur(p)) : fmtUsdApprox(toUsd(p));

// Plain numbers ("120000", "85.000", "85.000,00") count as ISK.
const parsePlain = (str) => {
  if (!str || !String(str).trim()) return null;
  if (!/^[\d.,\s -]+$/.test(String(str).trim())) return null; // pure text like "On request" stays untouched
  const n = parseNum(String(str).trim());
  return n === null ? null : { amount: n, currency: "ISK", prefix: "" };
};

// Lenient money parser: explicit currency first, plain numbers as ISK
export const parseMoney = (str) => parsePrice(str) || parsePlain(str);

// A price of exactly zero should display as nothing at all
export const isZeroPrice = (v) => {
  const p = parseMoney(v);
  return p ? p.amount === 0 : false;
};

// The price that counts toward totals: the client's picked room option when there is one
export const hotelPriceForTotal = (h) => {
  if (h?.picked_option && h?.room_options?.length) {
    const o = h.room_options.find((x) => x.option_id === h.picked_option);
    if (o?.price) return o.price;
  }
  return h?.price;
};

// dualPrice("From 950 EUR") -> { isk: "From 139.650 ISK", second: "≈ $1,019" } — null when unparseable.
// Plain numbers ("120000", "85.000") are treated as ISK — they gain period separators and a conversion.
export const dualPrice = (str, region) => {
  const p = parseMoney(str);
  if (!p) return null;
  return { isk: `${p.prefix}${fmtIsk(toIsk(p))}`, second: secondPrice(p, region) };
};

// Any money string -> ISK amount. Currency-coded values convert; plain numbers count as ISK.
export const moneyToIsk = (str) => {
  const expr = evalMoneyExpr(str); // quantity/arithmetic formulas ("3x230.000 ISK", "25.000*4") collapse to their total first
  const p = parseMoney(expr ?? str);
  if (p) return toIsk(p);
  return 0;
};

// Calculator fields: "25.000*4" -> "100.000", "85000+42000 ISK" -> "127.000 ISK", "2x 35000" -> "70.000".
// Thousand separators (. and ,) are stripped when they precede a 3-digit group; decimals like 2.5 survive.
// "x" between numbers multiplies (2 guests x 35.000 each = the 70.000 total).
// Returns null when the input is not a math expression, so plain prices pass through untouched.
export const evalMoneyExpr = (str) => {
  if (!str) return null;
  const s = String(str).trim();
  const curMatch = s.match(/\b(ISK|EUR|USD|GBP)\b\s*$/i);
  const cur = curMatch ? curMatch[1].toUpperCase() : "";
  const expr = (curMatch ? s.slice(0, curMatch.index) : s).trim();
  if (!/[+\-*/×÷]/.test(expr) && !/\d\s*[xX]\s*\d/.test(expr)) return null; // no operator -> not a calculation
  if (!/^[\d.,\s+\-*/()×÷xX]+$/.test(expr)) return null; // anything else (words, suffixes) -> leave as typed
  const normalized = expr
    .replace(/(\d)\s*[xX]\s*(?=\d)/g, "$1*") // 2x 35000 -> 2*35000
    .replace(/×/g, "*")
    .replace(/÷/g, "/")
    .replace(/[.,](?=\d{3}(\D|$))/g, "") // 25.000 -> 25000, 35,000 -> 35000, but 2.5 stays 2.5
    .replace(/,(\d{1,2})(?=\D|$)/g, ".$1"); // decimal comma: 25000,50 -> 25000.50
  try {
    const val = Function(`"use strict"; return (${normalized})`)();
    if (typeof val !== "number" || !Number.isFinite(val)) return null;
    const formatted = Math.round(val).toLocaleString("de-DE");
    return cur ? `${formatted} ${cur}` : formatted;
  } catch {
    return null;
  }
};

// Sum any list of price strings into ISK (skips unparseable text)
export const sumIsk = (strings) => strings.reduce((acc, s) => acc + moneyToIsk(s), 0);

// Per-day share of a total price across charged days — splitPrice("850 EUR", 4) -> "213 EUR"
export const splitPrice = (str, days) => {
  const p = parseMoney(str);
  if (!p || !days || days <= 0 || p.amount === 0) return null;
  const per = Math.round(p.amount / days);
  return `${p.prefix}${per.toLocaleString("de-DE")} ${p.currency}`;
};

// Day-aware share: the LAST charged day absorbs the rounding remainder so the days sum to the exact total.
// dayIndex is 1-based among charged days.
export const splitPriceDay = (str, days, dayIndex) => {
  const p = parseMoney(str);
  if (!p || !days || days <= 0 || p.amount === 0) return null;
  const base = Math.round(p.amount / days);
  const amt = dayIndex >= days ? p.amount - base * (days - 1) : base;
  return `${p.prefix}${amt.toLocaleString("de-DE")} ${p.currency}`;
};

// Charged days for a booking from startDay to endDay — the return/check-out day is free (24h rule)
export const chargedDays = (startDay, endDay) => Math.max(endDay - startDay, 0);

// European email domains -> EU client (EUR conversion). Everything else stays World (USD).
const EU_TLDS = new Set([
  "is", "uk", "de", "fr", "it", "es", "nl", "se", "no", "dk", "fi", "pl", "ie", "ch", "at", "be", "pt",
  "gr", "cz", "sk", "hu", "ro", "bg", "hr", "si", "ee", "lv", "lt", "lu", "mt", "cy", "eu", "fo", "gi",
  "ad", "mc", "sm", "va", "li", "al", "rs", "me", "mk", "ba", "md", "ua",
]);
export const isEuropeanEmail = (email) => {
  const domain = String(email || "").split("@")[1] || "";
  const tld = domain.toLowerCase().split(".").pop();
  return EU_TLDS.has(tld);
};


// Accent- and case-insensitive name key so "Kría Hótel" matches "Check-in — Kría Hotel"
export const nameKey = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();